/**
 * Sentralisert API-klient (P2).
 *
 * Hele frontend-en bruker i dag direkte `axios`-kall (~159 filer) mot
 * `${REACT_APP_BACKEND_URL}/api/...`. Denne modulen leverer:
 *
 *  1. `api` — én forhåndskonfigurert axios-instans med `baseURL` = `${BACKEND}/api`,
 *     Bearer-token-injeksjon og enhetlig feil-normalisering. NY kode bør bruke
 *     denne: `api.get('/bookings')` i stedet for `axios.get(\`${API}/bookings\`)`.
 *
 *  2. `installGlobalAxiosInterceptors()` — installerer de SAMME request/response-
 *     interceptorene på den globale `axios`-instansen, slik at ALLE eksisterende
 *     direkte `axios`-kall umiddelbart får samme auth- og feilhåndtering uten at
 *     vi må skrive om alle 159 filene. Kalles én gang ved oppstart (i App.tsx).
 *
 *  3. `getApiErrorMessage()` — felles hjelper for å trekke ut en lesbar
 *     feilmelding (FastAPI `detail`, axios `message`, fallback).
 *
 * Auth-modell (uendret): Bearer-token i localStorage('access_token'),
 * `withCredentials = false` (Emergent-ingress returnerer `ACAO: *`, som er
 * inkompatibel med credentials-modus).
 */
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

/** Trekk ut en lesbar feilmelding fra en axios-feil. */
export function getApiErrorMessage(error: unknown, fallback = 'Noe gikk galt. Prøv igjen.'): string {
  const err = error as AxiosError<any> | undefined;
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  // FastAPI validation errors come back as an array of {msg, loc}
  if (Array.isArray(detail) && detail.length && detail[0]?.msg) return detail[0].msg;
  const message = err?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) return message;
  if (err?.message) return err.message;
  return fallback;
}

/** Legg på Bearer-token fra localStorage på hver request (kilde til sannhet). */
function attachAuthToken(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if (typeof window === 'undefined') return config;
  const token = localStorage.getItem('access_token');
  if (token && config.headers && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}

/**
 * Normaliser feil: fester `userMessage` på feilobjektet (opt-in for call-sites),
 * og varsler resten av appen om 401 via et ikke-forstyrrende window-event.
 * Re-rejecter alltid, så eksisterende `.catch`-blokker kjører som før.
 */
function normalizeError(error: AxiosError): Promise<never> {
  (error as any).userMessage = getApiErrorMessage(error);
  if (error.response?.status === 401 && typeof window !== 'undefined') {
    // Ikke redirect her (App.tsx håndterer auth-state) — bare kringkast for
    // de som vil lytte. Unngår redirect-løkker.
    window.dispatchEvent(new CustomEvent('api:unauthorized'));
  }
  return Promise.reject(error);
}

/** Anvend de delte interceptorene på en gitt axios-instans. */
function applyInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use(attachAuthToken);
  instance.interceptors.response.use((res) => res, normalizeError);
}

/** Midlertidige feil fra nett/proxy/CDN (tom respons, 502/503/504, Cloudflare 52x, 429). */
export function isTransientApiError(error: unknown): boolean {
  const err = error as AxiosError | undefined;
  if (!err || !axios.isAxiosError(err)) return false;
  if (!err.response) return err.code !== 'ERR_CANCELED';
  const s = err.response.status;
  return s === 429 || s === 502 || s === 503 || s === 504 || (s >= 520 && s <= 527);
}

/** Kjør et idempotent API-kall på nytt ved midlertidige feil (standard 3 forsøk, 800 ms → 1600 ms). */
export async function retryTransient<T>(fn: () => Promise<T>, attempts = 3, baseDelayMs = 800): Promise<T> {
  let lastError: unknown;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (!isTransientApiError(error) || i === attempts - 1) throw error;
      await new Promise((r) => setTimeout(r, baseDelayMs * (i + 1)));
    }
  }
  throw lastError;
}

/** Den sentrale klienten for ny kode. */
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
});
applyInterceptors(api);

let installed = false;
/** Installer de delte interceptorene på den globale axios-instansen (én gang). */
export function installGlobalAxiosInterceptors(): void {
  if (installed) return;
  installed = true;
  axios.defaults.withCredentials = false;
  applyInterceptors(axios);
}

export default api;
